<template>
  <div
    class="variant-image-upload"
    :class="{ 'has-image': !!src, 'is-dragging': dragging, 'is-disabled': disabled }"
    :style="{ width: `${size}px`, height: `${size}px` }"
    :title="disabled ? '' : (src ? 'Change variant image' : 'Upload variant image')"
    @click="openPicker"
    @dragover.prevent="!disabled && (dragging = true)"
    @dragleave.prevent="dragging = false"
    @drop.prevent="onDrop"
  >
    <input
      ref="fileInput"
      type="file"
      class="d-none"
      accept="image/jpeg,image/png,image/webp"
      @click.stop
      @change="onPick"
    />

    <template v-if="src">
      <img :src="displaySrc" alt="Variant image" />
      <div v-if="!disabled" class="variant-image-actions">
        <button type="button" title="Change image" @click.stop="openPicker">
          <i class="ti ti-refresh"></i>
        </button>
        <button type="button" title="Remove image" @click.stop="$emit('remove')">
          <i class="ti ti-trash"></i>
        </button>
      </div>
    </template>

    <div v-else class="variant-image-empty">
      <i class="ti ti-photo-plus"></i>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

export default {
  name: 'VariantImageUpload',
  props: {
    /** Current image: a blob: preview URL or a media path/URL from the API. */
    src: {
      type: String,
      default: '',
    },
    size: {
      type: Number,
      default: 56,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select', 'remove'],
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      dragging: false,
    };
  },
  computed: {
    displaySrc() {
      return this.src.startsWith('blob:') ? this.src : this.$getImageUrl(this.src);
    },
  },
  methods: {
    openPicker() {
      if (this.disabled) return;
      this.$refs.fileInput.click();
    },

    onPick(event) {
      this.handleFile(event.target.files?.[0]);
      // Allow picking the same file again after a remove.
      event.target.value = '';
    },

    onDrop(event) {
      this.dragging = false;
      if (this.disabled) return;
      this.handleFile(event.dataTransfer?.files?.[0]);
    },

    handleFile(file) {
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        this.toast.error('Only JPG, PNG, and WEBP images are allowed.');
        return;
      }
      if (file.size > MAX_BYTES) {
        this.toast.error('Variant image must be 2 MB or smaller.');
        return;
      }
      this.$emit('select', file);
    },
  },
};
</script>

<style scoped>
.variant-image-upload {
  position: relative;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1.5px dashed #c7d2e0;
  background: #f8fafc;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.variant-image-upload:hover,
.variant-image-upload.is-dragging {
  border-color: #fe9f43;
  background: #fff7ef;
  box-shadow: 0 0 0 3px rgba(254, 159, 67, 0.15);
}

.variant-image-upload.has-image {
  border-style: solid;
  border-color: #e5eaf2;
  background: #ffffff;
}

.variant-image-upload.is-disabled {
  cursor: default;
  box-shadow: none;
}

.variant-image-upload img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.variant-image-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 22px;
  transition: color 0.15s ease;
}

.variant-image-upload:hover .variant-image-empty,
.variant-image-upload.is-dragging .variant-image-empty {
  color: #fe9f43;
}

.variant-image-actions {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(15, 23, 42, 0.55);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.variant-image-upload:hover .variant-image-actions {
  opacity: 1;
}

.variant-image-actions button {
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: #ffffff;
  color: #1f2937;
  font-size: 13px;
  line-height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.variant-image-actions button:hover {
  background: #fe9f43;
  color: #ffffff;
}
</style>
